import { Actions, createEffect, ofType } from '@ngrx/effects';
import { accountActions, accountAPIActions } from './account.state';
import { inject, isDevMode } from '@angular/core';
import { catchError, EMPTY, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { AlertService } from 'app/core/util/alert.service';
import { StateStorageService } from 'app/core/auth/state-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { TravomateAccountService } from 'app/travomate/core/auth/travomate.account.service';

const accountEffect = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(TravomateAccountService),
    stateStorageService = inject(StateStorageService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(accountAPIActions.currentUser),
      mergeMap(() => service.fetchCurrent()),
      tap(account => {
        if (!stateStorageService.getLocale()) {
          translateService.use(account.lang?.isoCode || translateService.getDefaultLang());
        }
      }),
      switchMap(currentUser => of(accountAPIActions.currentUserSuccess({ currentUser }), accountActions.setUserAuthorities())),
      catchError(() => of(accountAPIActions.currentUserFailed())),
    ),
  { functional: true },
);

const setUserAuthoritiesEffect = createEffect(
  (actions$ = inject(Actions), service = inject(TravomateAccountService)) =>
    actions$.pipe(
      ofType(accountActions.setUserAuthorities),
      mergeMap(() => service.getAuthorities()),
      map(authorities => accountActions.setUserAuthoritiesSuccess({ authorities })),
      catchError(err => {
        if (isDevMode()) {
          console.error('Could not load authorities', err);
        }
        return EMPTY;
      }),
    ),
  { functional: true },
);

const accountFailedEffect = createEffect(
  (actions$ = inject(Actions), service = inject(AlertService)) =>
    actions$.pipe(
      ofType(accountAPIActions.currentUserFailed),
      tap(() =>
        service.addAlert({
          type: 'danger',
          message: 'Could not load current user',
        }),
      ),
      map(() => accountAPIActions.currentUserFailedHandled()),
    ),
  { functional: true },
);

const logoutEffect = createEffect(
  (actions$ = inject(Actions), authenticatorService = inject(AuthenticatorService)) =>
    actions$.pipe(
      ofType(accountActions.logout),
      tap(() => authenticatorService.signOut()),
      map(() => accountActions.logoutSuccess()),
    ),
  { functional: true },
);

export default { accountEffect, accountFailedEffect, setUserAuthoritiesEffect, logoutEffect };
